// src/utils/grnModel.js

// Payment method constants
export const PAYMENT_METHODS = {
  CASH: 'cash',
  CHEQUE: 'cheque',
  BANK_TRANSFER: 'bank_transfer',
  CARD: 'card'
}

// GRN Status constants
export const GRN_STATUS = {
  PENDING: 'pending',
  PARTIAL: 'partial',
  COMPLETE: 'complete',
  CANCELLED: 'cancelled'
}

// Payment status constants
export const PAYMENT_STATUS = {
  UNPAID: 'unpaid',
  PARTIALLY_PAID: 'partially_paid',
  FULLY_PAID: 'fully_paid'
}

// GRN Model class for business logic
export class GRNModel {
  /**
   * Generate GRN number
   */
  static generateGRNNumber() {
    return `GRN-${Date.now()}`
  }

  /**
   * Determine GRN status based on received items
   * @param {Array} receivedItems - Array of received items
   * @param {Object} grn - GRN object
   * @returns {string} - Status string
   */
  static determineStatus(receivedItems = [], grn = {}) {
    if (!Array.isArray(receivedItems) || receivedItems.length === 0) {
      return GRN_STATUS.PENDING
    }

    let completeItems = 0
    let partialItems = 0
    let incompleteItems = 0

    receivedItems.forEach(item => {
      const ordered = parseInt(item.orderedQuantity) || 0
      const received = parseInt(item.receivedQuantity) || 0

      if (received === 0) {
        incompleteItems++
      } else if (received >= ordered) {
        completeItems++
      } else {
        partialItems++
      }
    })

    // Update GRN with calculated values
    if (grn) {
      grn.completedItems = completeItems
      grn.partialItems = partialItems
      grn.incompleteItems = incompleteItems
    }

    // Determine overall status
    if (completeItems === receivedItems.length) {
      return GRN_STATUS.COMPLETE
    } else if (incompleteItems === receivedItems.length) {
      return GRN_STATUS.PENDING
    } else {
      return GRN_STATUS.PARTIAL
    }
  }

  /**
   * Get status configuration for display
   * @param {string} status - Status string
   * @returns {Object} - Status configuration
   */
  static getStatusConfig(status) {
    const configs = {
      [GRN_STATUS.PENDING]: {
        label: 'Pending',
        icon: '⏳',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      },
      [GRN_STATUS.PARTIAL]: {
        label: 'Partial',
        icon: '📦',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200'
      },
      [GRN_STATUS.COMPLETE]: {
        label: 'Complete',
        icon: '✅',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      [GRN_STATUS.CANCELLED]: {
        label: 'Cancelled',
        icon: '❌',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      }
    }

    return configs[status] || configs[GRN_STATUS.PENDING]
  }

  /**
   * Calculate payment status based on amounts
   * @param {number} totalAmount - Total GRN amount
   * @param {number} paidAmount - Amount already paid
   * @returns {string} - Payment status
   */
  static calculatePaymentStatus(totalAmount, paidAmount) {
    const total = parseFloat(totalAmount) || 0
    const paid = parseFloat(paidAmount) || 0

    if (paid === 0) {
      return PAYMENT_STATUS.UNPAID
    } else if (paid >= total) {
      return PAYMENT_STATUS.FULLY_PAID
    } else {
      return PAYMENT_STATUS.PARTIALLY_PAID
    }
  }

  /**
   * Get payment status configuration for display
   * @param {string} status - Payment status string
   * @returns {Object} - Payment status configuration
   */
  static getPaymentStatusConfig(status) {
    const configs = {
      [PAYMENT_STATUS.UNPAID]: {
        label: 'Unpaid',
        icon: '💳',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      },
      [PAYMENT_STATUS.PARTIALLY_PAID]: {
        label: 'Partially Paid',
        icon: '💰',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      },
      [PAYMENT_STATUS.FULLY_PAID]: {
        label: 'Fully Paid',
        icon: '✅',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      }
    }

    return configs[status] || configs[PAYMENT_STATUS.UNPAID]
  }

  /**
   * Validate GRN data
   * @param {Object} grnData - GRN data to validate
   * @returns {Object} - Validation result with errors object
   */
  static validate(grnData) {
    const errors = {}

    if (!grnData.purchaseOrder) {
      errors.purchaseOrder = 'Purchase order is required'
    }

    if (!grnData.receivedBy || typeof grnData.receivedBy !== 'string' || !grnData.receivedBy.trim()) {
      errors.receivedBy = 'Received by is required'
    }

    if (!grnData.receivedDate) {
      errors.receivedDate = 'Received date is required'
    }

    if (!grnData.receivedItems || !Array.isArray(grnData.receivedItems) || grnData.receivedItems.length === 0) {
      errors.receivedItems = 'At least one received item is required'
    }

    // Validate received items
    if (grnData.receivedItems && Array.isArray(grnData.receivedItems)) {
      const hasReceivedQuantity = grnData.receivedItems.some(item => 
        typeof item.receivedQuantity === 'number' && item.receivedQuantity > 0
      )
      
      if (!hasReceivedQuantity) {
        errors.receivedItems = 'Please enter received quantities for at least one item'
      }

      grnData.receivedItems.forEach((item, index) => {
        if (!item.poItemId) {
          errors[`receivedItems.${index}.poItemId`] = `Item ${index + 1}: Purchase order item ID is required`
        }

        if (typeof item.receivedQuantity !== 'number' || item.receivedQuantity < 0) {
          errors[`receivedItems.${index}.receivedQuantity`] = `Item ${index + 1}: Valid received quantity is required`
        }

        if (typeof item.unitPrice !== 'number' || item.unitPrice < 0) {
          errors[`receivedItems.${index}.unitPrice`] = `Item ${index + 1}: Valid unit price is required`
        }
      })
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  /**
   * Calculate GRN totals
   * @param {Array} receivedItems - Array of received items
   * @returns {Object} - Calculated totals
   */
  static calculateTotals(receivedItems = []) {
    let totalReceived = 0
    let completeItems = 0
    let partialItems = 0
    let incompleteItems = 0

    receivedItems.forEach(item => {
      const ordered = parseInt(item.orderedQuantity) || 0
      const received = parseInt(item.receivedQuantity) || 0
      const unitPrice = parseFloat(item.unitPrice) || 0

      // Add to total amount
      totalReceived += received * unitPrice

      // Categorize items
      if (received === 0) {
        incompleteItems++
      } else if (received >= ordered) {
        completeItems++
      } else {
        partialItems++
      }
    })

    return {
      totalReceived: Math.round(totalReceived * 100) / 100, // Round to 2 decimal places
      completeItems,
      partialItems,
      incompleteItems,
      totalItems: receivedItems.length
    }
  }

  /**
   * Format GRN data for API - FINAL CORRECT VERSION
   * @param {Object} formData - Form data from GRN creation form
   * @returns {Object} - API-formatted GRN data
   */
  static formatForAPI(formData) {
    // Filter items that have received quantities > 0 and have poItemId
    const receivedItems = (formData.receivedItems || [])
      .filter(item => item.poItemId && (parseInt(item.receivedQuantity) || 0) > 0)
      .map(item => {
        const itemData = {
          poItemId: item.poItemId,
          receivedQuantity: parseInt(item.receivedQuantity) || 0,
          unitPrice: parseFloat(item.unitPrice) || 0
        }
        
        // Include expiry date if provided
        if (item.expiryDate && item.expiryDate.trim() !== '') {
          itemData.expiryDate = item.expiryDate
        }
        
        return itemData
      })

    console.log('📦 GRNModel.formatForAPI - receivedItems:', receivedItems)

    return {
      purchaseOrder: formData.purchaseOrder,
      receivedBy: formData.receivedBy,
      receivedDate: formData.receivedDate,
      receivedItems,
      note: formData.note || ''
    }
  }

  /**
   * Format currency amount
   * @param {number} amount - Amount to format
   * @returns {string} - Formatted currency string
   */
  static formatCurrency(amount) {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  /**
   * Format date for display
   * @param {string|Date} date - Date to format
   * @returns {string} - Formatted date string
   */
  static formatDate(date) {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  /**
   * Format date and time for display
   * @param {string|Date} date - Date to format
   * @returns {string} - Formatted date and time string
   */
  static formatDateTime(date) {
    if (!date) return 'N/A'
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
}

export default GRNModel